import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Mail, Phone } from 'lucide-react';
import Footer from '../components/Footer';

export default function RefundPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#161616] text-white flex flex-col">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-[#161616]/80 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            data-testid="refund-back-btn"
            className="text-xl sm:text-2xl font-bold tracking-tight cursor-pointer"
            style={{ fontFamily: 'Clash Display' }}
          >
            Move<span className="text-[#C6A97C]">More</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition"
            data-testid="refund-home-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
        </div>
      </nav>

      <section className="pt-32 pb-16 px-6 flex-1">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#D9C7A3] flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-[#0B0B0B]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#C6A97C] mb-1">OUR PROMISE</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: 'Clash Display' }}>
                  30-Day Money-Back Guarantee
                </h1>
              </div>
            </div>

            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              We want every client to feel confident in their recovery journey. If our programs aren't the right fit for you, we'll refund your purchase — no awkward conversations, no fine-print games.
            </p>

            <div className="space-y-8 text-gray-200">
              <PolicySection title="What's covered">
                <ul className="space-y-2 list-disc pl-5 text-gray-300">
                  <li>Monthly and Annual subscriptions</li>
                  <li>One-time individual course purchases</li>
                </ul>
              </PolicySection>

              <PolicySection title="How it works">
                <ol className="space-y-3 list-decimal pl-5 text-gray-300">
                  <li>Email <a href="mailto:brandon@burkebiokinetics.co.za" className="text-[#C6A97C] hover:underline">brandon@burkebiokinetics.co.za</a> within <strong className="text-white">30 days</strong> of your purchase date.</li>
                  <li>Include the email address used at checkout and your order ID (from your receipt email).</li>
                  <li>We'll process your refund to the original payment method within <strong className="text-white">5–10 business days</strong>.</li>
                  <li>Your access to the program ends on the day the refund is issued.</li>
                </ol>
              </PolicySection>

              <PolicySection title="What we ask">
                <p className="text-gray-300 leading-relaxed">
                  We'd love a quick line on what didn't work for you — it's the only way we get better. But it's not required to receive your refund.
                </p>
              </PolicySection>

              <PolicySection title="Exclusions">
                <ul className="space-y-2 list-disc pl-5 text-gray-300">
                  <li>Refund requests made more than 30 days after the original purchase.</li>
                  <li>Annual subscriptions in their second renewal year (the 30-day window applies to your first paid year only).</li>
                  <li>Refunds requested due to inability to access the platform without first contacting support so we can resolve the issue.</li>
                </ul>
              </PolicySection>

              <PolicySection title="Subscription cancellations">
                <p className="text-gray-300 leading-relaxed">
                  You can cancel a recurring subscription at any time from your dashboard or by emailing us. Cancelling stops future billing — refunds for the current paid period are governed by the 30-day policy above.
                </p>
              </PolicySection>

              <PolicySection title="Questions?">
                <div className="space-y-2 text-gray-300">
                  <a href="mailto:brandon@burkebiokinetics.co.za" className="inline-flex items-center gap-2 hover:text-[#C6A97C] transition">
                    <Mail className="w-4 h-4" /> brandon@burkebiokinetics.co.za
                  </a>
                  <br/>
                  <a href="https://wa.me/27822532520" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#C6A97C] transition">
                    <Phone className="w-4 h-4" /> WhatsApp +27 82 253 2520
                  </a>
                </div>
              </PolicySection>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10 text-sm text-gray-500">
              <p>Burke Biokinetics &middot; 76 Reagent Road, Sea Point</p>
              <p className="mt-1">Last updated: April 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PolicySection({ title, children }) {
  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold mb-3 text-white" style={{ fontFamily: 'Clash Display' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}
